import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProduct } from '../context/ProductContext';
import {
  LayoutDashboard,
  Users,
  FileText,
  Wallet,
  Settings,
  LogOut,
  Zap,
  TrendingUp,
  ChevronRight,
  ClipboardList,
  Activity,
  Package,
  Filter,
  Beaker,
  Home
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/products', icon: Package, label: 'Products & Services' },
  { path: '/leads', icon: Users, label: 'Leads' },
  { path: '/proposals', icon: FileText, label: 'Proposals' },
  { path: '/deals', icon: TrendingUp, label: 'Deals' },
  { path: '/pending-actions', icon: ClipboardList, label: 'Action Queue', adminOnly: true },
  { path: '/simulations', icon: Beaker, label: 'Sandbox Simulator', adminOnly: true },
  { path: '/wallet', icon: Wallet, label: 'Wallet' },
  { path: '/usage-log', icon: Activity, label: 'Usage Log', adminOnly: true },
  { path: '/settings', icon: Settings, label: 'Settings', adminOnly: true }
];

export const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => 
    !item.adminOnly || isAdmin()
  );

  return (
    <aside className="sidebar" data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg tracking-tight">EmpowerBiz AI</h1>
            <p className="text-xs text-slate-400">AI Business Growth Platform</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="space-y-1">
          <NavLink
            to="/platform"
            data-testid="nav-platform-home"
            className={({ isActive }) =>
              `sidebar-nav-item ${isActive ? 'active' : ''}`
            }
          >
            <Home className="w-5 h-5" />
            <span>Home</span>
            <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
          </NavLink>

          <div className="px-3 pt-5 pb-2">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <span className="h-px flex-1 bg-slate-800" />
              <span>AI Sales Automation</span>
              <span className="h-px flex-1 bg-slate-800" />
            </div>
          </div>

          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? 'active' : ''}`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </NavLink>
          ))}
        </div>
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
            <span className="text-sm font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate capitalize">
              {user?.role?.replace('_', ' ')}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 
                     hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
};

export const MainLayout = ({ children }) => {
  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      <Sidebar />
      <main className="main-content flex-1 overflow-y-auto">
        <GlobalProductFilter />
        {children}
      </main>
    </div>
  );
};

export const GlobalProductFilter = () => {
  const { products, selectedProduct, setSelectedProduct } = useProduct();
  // Only show if at least one product exists
  if (!products || products.length === 0) return null;
  return (
    <div className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200 px-6 py-2 flex items-center gap-3" data-testid="global-product-filter">
      <Filter className="w-4 h-4 text-slate-500" />
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
        Filter
      </span>
      <select
        value={selectedProduct}
        onChange={(e) => setSelectedProduct(e.target.value)}
        data-testid="global-product-filter-select"
        className="text-sm px-3 py-1.5 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
      >
        <option value="all">All Products / Services</option>
        <option value="unassigned">Unassigned</option>
        {products.filter(p => p.is_active).map(p => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
      <span className="text-xs text-slate-400">Filter applies to Dashboard, Leads, Deals, Proposals, Action Queue, Wallet, Usage Log</span>
    </div>
  );
};

export default MainLayout;
